import type {
  AdyenCheckoutError,
  CoreConfiguration,
  PaymentAction as AdyenPaymentAction,
  ResultCode,
  UIElement,
} from '@adyen/adyen-web';
import { AdyenCheckout, ApplePay } from '@adyen/adyen-web';
import Decimal from 'decimal.js';
import type { Router } from 'next/router';
import type { ParsedUrlQuery } from 'querystring';
import type { Dispatch, SetStateAction } from 'react';
import type { MessageDescriptor } from 'react-intl';
import type { UrlObject } from 'url';

import type {
  CheckoutFragment,
  TransactionInitializeMutation,
  TransactionItemFragment,
  TransactionProcessMutation,
} from '@/graphql/saleor/api';
import {
  ADYEN_CLIENT_KEY,
  ADYEN_ENV,
  ADYEN_EU_ACCOUNTS,
  ADYEN_MERCHANT_ACCOUNT_AU,
  ADYEN_MERCHANT_ACCOUNT_CA,
  ADYEN_MERCHANT_ACCOUNT_CH,
  ADYEN_MERCHANT_ACCOUNT_EU,
  ADYEN_MERCHANT_ACCOUNT_JP,
  ADYEN_MERCHANT_ACCOUNT_UK,
  ADYEN_MERCHANT_ACCOUNT_US,
  GOOGLE_PAY_ENV,
  GOOGLE_PAY_MERCHANT_ID,
  GOOGLE_PAY_MERCHANT_NAME,
  IS_BROWSER,
} from '@/lib/const';
import type { GenericErrorHandler } from '@/lib/forms/helpers';
import { capturePaymentAnomaly } from '@/lib/payment/anomaly';
import type { Maybe } from '@/lib/types';
import { paymentMessages } from '@/messages/payment';
import { theme } from '@/theme';

import { fetchAndSetCookies, ORICO_PAYMENT_URL } from './oricoFinancing';
import { oneyPaymentTypePattern } from './paymentTypeChecks';
import {
  type AdyenCheckoutOpts,
  type AdyenEnv,
  type MulticardConfig,
  PAYMENT_TYPES,
  type PaymentType,
} from './types';

export {
  checkIsOneyPaymentByType,
  isNotSupportMulticardPaymentMethod,
  isTokenizablePaymentMethod,
} from './paymentTypeChecks';

let cachedApplePayAvailability: boolean | null = null;

export const APPLE_PAY_AVAILABILITY_SESSION_KEY = 'applePayAvailable';

const readStoredApplePayAvailability = (): boolean | null => {
  try {
    const stored = sessionStorage.getItem(APPLE_PAY_AVAILABILITY_SESSION_KEY);
    return stored === null ? null : stored === 'true';
  } catch {
    return null;
  }
};

const storeApplePayAvailability = (isAvailable: boolean) => {
  cachedApplePayAvailability = isAvailable;
  try {
    sessionStorage.setItem(
      APPLE_PAY_AVAILABILITY_SESSION_KEY,
      String(isAvailable),
    );
  } catch {
    // Storage blocked: the in-memory cache still covers this page load.
  }
};

/**
 * Checks whether Apple Pay is available in the current environment.
 *  - Only run in the browser. If server-side, return false.
 *  - Use the result already found during this visit (avoid repeated checks).
 *  - Return `true` for Safari
 *  - Use Adyen's ApplePay component to check availability
 */
export const checkApplePayAvailability = async (): Promise<boolean> => {
  if (!IS_BROWSER) {
    return false;
  }

  if (cachedApplePayAvailability !== null) {
    return cachedApplePayAvailability;
  }

  if (!window.isSecureContext) {
    cachedApplePayAvailability = false;
    return false;
  }

  const storedAvailability = readStoredApplePayAvailability();
  if (storedAvailability !== null) {
    cachedApplePayAvailability = storedAvailability;
    return storedAvailability;
  }

  try {
    // Quick Safari check (native Apple Pay)
    if (
      'ApplePaySession' in window &&
      window.ApplePaySession.canMakePayments()
    ) {
      storeApplePayAvailability(true);
      return true;
    }

    // Initialize a minimal AdyenCheckout instance
    const adyenCheckout = await AdyenCheckout({
      environment: ADYEN_ENV as AdyenEnv,
      clientKey: ADYEN_CLIENT_KEY,
      countryCode: 'US',
      amount: {
        value: 0,
        currency: 'USD',
      },
    });

    // Create Apple Pay component (without rendering a button)
    const adyenApplePayComponent = new ApplePay(adyenCheckout, {
      showPayButton: false,
    });

    // Adyen's isAvailable() will resolve if Apple Pay is supported:
    // - Safari native Apple Pay
    // - Chrome/Edge/Firefox on macOS (QR-code flow)
    await adyenApplePayComponent.isAvailable();

    storeApplePayAvailability(true);
    return true;
  } catch {
    storeApplePayAvailability(false);
    return false;
  }
};

export const toMinorPrice = (amount: number) => Math.round(amount * 100);

export const getMerchantAccount = (countryCode: string) => {
  if (ADYEN_EU_ACCOUNTS?.includes(countryCode)) {
    return ADYEN_MERCHANT_ACCOUNT_EU;
  }

  switch (countryCode) {
    case 'US':
      return ADYEN_MERCHANT_ACCOUNT_US;
    case 'CA':
      return ADYEN_MERCHANT_ACCOUNT_CA;
    case 'CH':
      return ADYEN_MERCHANT_ACCOUNT_CH;
    case 'UK':
      return ADYEN_MERCHANT_ACCOUNT_UK;
    case 'JP':
      return ADYEN_MERCHANT_ACCOUNT_JP;
    case 'AU':
      return ADYEN_MERCHANT_ACCOUNT_AU;
    default:
      return ADYEN_MERCHANT_ACCOUNT_US;
  }
};

export const getTranslatedPaymentMethodName = (type: PaymentType) => {
  const nameMapping: Record<PaymentType, MessageDescriptor> = {
    card: paymentMessages.card,
    alipay: paymentMessages.aliPay,
    applepay: paymentMessages.applePay,
    applepay_express: paymentMessages.applePayExpress,
    googlepay: paymentMessages.googlePay,
    paywithgoogle: paymentMessages.paywithgoogle,
    unionpay: paymentMessages.unionPay,
    paypal: paymentMessages.payPal,
    paypal_express: paymentMessages.payPalExpress,
    klarna_paynow: paymentMessages.klarna_paynow,
    klarna_account: paymentMessages.klarna_account,
    ideal: paymentMessages.ideal,
    twint: paymentMessages.twint,
    affirm: paymentMessages.affirm,
    scheme: paymentMessages.creditCard,
    visa: paymentMessages.visa,
    bcmc: paymentMessages.bancontact,
    facilypay_3x: paymentMessages.oney,
    facilypay_4x: paymentMessages.oney,
    paybybank: paymentMessages.paybybank,
    paybybank_AIS_DD: paymentMessages.paybybankUS,
    v12: paymentMessages.v12,
    cembra_installment: paymentMessages.cembra,
    cembra_invoice: paymentMessages.cembra,
    creditplus: paymentMessages.creditPlus,
    agos: paymentMessages.agos,
    zip: paymentMessages.zip,
    offline_banktransfer: paymentMessages.bankTransfer,
    orico_financing: paymentMessages.oricoFinancing,
    smbc: paymentMessages.smbc,
  };
  return nameMapping[type];
};

export const PAYMENT_QUERY_PARAMS = {
  additionalAction: 'additional-action-status',
  orderId: 'order-id',
  checkoutId: 'checkout-id',
  errorCode: 'error-code',
  failureMulticardMaxPayments: 'failure-multicard-max-payments',
  failureMulticardMinAmount: 'failure-next-payment-min-amount',
  failureMulticardMaxAmount: 'failure-next-payment-max-amount',
  successMulticardMaxPayments: 'success-multicard-max-payments',
  successMulticardMinAmount: 'success-next-payment-min-amount',
  successMulticardMaxAmount: 'success-next-payment-max-amount',
  payPerLinkToken: 'pay-per-link-token',
  transactionId: 'transaction-id',
} as const;

const NON_ADYEN_PAYMENT_METHODS: Readonly<PaymentType[]> = [
  'v12',
  'cembra_installment',
  'cembra_invoice',
  'creditplus',
  'agos',
  PAYMENT_TYPES.OFFLINE,
  'orico_financing',
  'smbc',
];

const REDIRECT_PAYMENT_METHODS: Readonly<PaymentType[]> = [PAYMENT_TYPES.TWINT];

export const PAYMENT_REDIRECT_SESSION_KEY = 'paymentRedirectExpected';

export const markPaymentRedirectExpected = (): void => {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(PAYMENT_REDIRECT_SESSION_KEY, '1');
  }
};

export const wasPaymentRedirectExpected = (): boolean =>
  typeof sessionStorage !== 'undefined' &&
  sessionStorage.getItem(PAYMENT_REDIRECT_SESSION_KEY) === '1';

export const clearPaymentRedirectExpected = (): void => {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(PAYMENT_REDIRECT_SESSION_KEY);
  }
};

export const isRedirectPaymentMethod = (type: PaymentType | undefined) =>
  type !== undefined && REDIRECT_PAYMENT_METHODS.includes(type);

export const isTokenizablePaymentMethodWithoutPayButton = (
  type: PaymentType | undefined,
) => {
  return (
    type &&
    (
      [
        PAYMENT_TYPES.APPLE_PAY,
        PAYMENT_TYPES.GOOGLE_PAY,
        PAYMENT_TYPES.PAY_WITH_GOOGLE,
      ] as PaymentType[]
    )?.includes(type)
  );
};

export const getAdyenComponentConfig = (
  type: PaymentType,
  price: { amount: number; currency: string },
  countryCode: string,
) => {
  switch (type) {
    case 'card':
    case 'scheme':
      return {
        hasHolderName: true,
        holderNameRequired: true,
        showContextualElement: false,
        showStoredPaymentMethods: false,
        amount: {
          value: 0, // Backend sends the correct value to Adyen
          currency: price.currency,
        },
        styles: {
          base: {
            fontSize: theme.fontSizes.p1,
            color: theme.colors.black,
          },
          placeholder: {
            fontSize: theme.fontSizes.p1,
          },
          error: {
            color: theme.colors.error,
          },
          validated: {
            color: theme.colors.black,
          },
        },
      };
    case 'paywithgoogle':
    case 'googlepay':
      return {
        environment: GOOGLE_PAY_ENV,
        configuration: {
          gatewayMerchantId: getMerchantAccount(countryCode),
          merchantId: GOOGLE_PAY_MERCHANT_ID,
          merchantName: GOOGLE_PAY_MERCHANT_NAME,
        },
        countryCode,
        amount: {
          value: price.amount,
          currency: price.currency,
        },
        showPayButton: false,
      };
    case 'applepay':
    case 'bcmc':
      return {
        showPayButton: false,
      };
    default:
      return {
        styles: {
          base: {
            fontSize: theme.fontSizes.p1,
            color: theme.colors.black,
          },
          placeholder: {
            fontSize: theme.fontSizes.p1,
          },
          error: {
            color: theme.colors.error,
          },
          validated: {
            color: theme.colors.black,
          },
        },
      };
  }
};

// Adyen reports a blocked translations download (ad blocker, proxy, flaky network)
// through onError but still renders the form, so it is not a payment failure.
const isTranslationsFetchError = ({ message }: AdyenCheckoutError) =>
  message.startsWith('Translations:') ||
  message === 'Failed to fetch translation';

export const createAdyenCheckout = async ({
  countryCode,
  formatMessage,
  locale,
  onError,
  paymentMethodType,
  price,
  showPayButton,
  ...handlers
}: AdyenCheckoutOpts) => {
  const defaultTranslations = {
    [locale]: {
      'creditCard.securityCode.label': formatMessage(
        paymentMessages.creditCardSecurityCode,
      ),
      'creditCard.expiryDate.label': formatMessage(
        paymentMessages.creditCardExpiryDate,
      ),
      'creditCard.cardNumber.label': formatMessage(
        paymentMessages.creditCardNumber,
      ),
      'creditCard.holderName': formatMessage(
        paymentMessages.creditCardHolderName,
      ),
    },
  };
  const translations = {
    [locale]: {
      ...defaultTranslations[locale],
      ...(paymentMethodType === PAYMENT_TYPES.BANCONTACT
        ? {
            'creditCard.cardNumber.label': formatMessage(
              paymentMessages.cardNumber,
            ),
          }
        : {}),
    },
  };
  const configuration: CoreConfiguration = {
    amount: {
      value: price.amount, // may need to be align with https://docs.adyen.com/development-resources/currency-codes/#page-introduction
      currency: price.currency,
    },
    environment: ADYEN_ENV as AdyenEnv,
    clientKey: ADYEN_CLIENT_KEY,
    analytics: {
      enabled: true,
    },
    locale,
    translations,
    showPayButton,
    countryCode,
    onError: (error, element) => {
      if (isTranslationsFetchError(error)) {
        return;
      }
      onError?.(error, element);
    },
    ...handlers,
  };

  return await AdyenCheckout(configuration);
};

interface ExtendedPaymentAction extends AdyenPaymentAction {
  data?: Record<string, string>;
}

type PathCode =
  | Exclude<ResultCode, 'RedirectShopper' | 'IdentifyShopper'>
  | 'default';
export const handleTransactionResponse = async <
  T extends Maybe<Record<string, any>>,
>(
  transactionData: T,
  {
    amountRemainingToBePaid,
    errorHandler,
    handleAction,
    isReload,
    onAuthorize,
    paymentType,
    push,
    redirectPaths,
    reload,
    setIsTransactionSuccessfully,
  }: {
    amountRemainingToBePaid?: boolean;
    errorHandler: GenericErrorHandler;
    handleAction?: UIElement['handleAction'];
    isReload?: boolean;
    onAuthorize?: (data: T) => void;
    paymentType?: PaymentType;
    push?: Router['push'];
    redirectPaths?: Partial<Record<PathCode, UrlObject>>;
    reload?: Router['reload'];
    setIsTransactionSuccessfully?: Dispatch<SetStateAction<boolean>>;
  },
) => {
  const resultCode: ResultCode =
    transactionData?.resultCode ??
    (!amountRemainingToBePaid ? 'Authorised' : 'Unknown');

  const redirect = async (code: PathCode) => {
    if (!push) {
      return null;
    }

    if (isReload && reload) {
      return reload();
    }

    const path = redirectPaths?.[code];
    const defaultPath = redirectPaths?.default;

    if (path) {
      return await push(path);
    }

    if (defaultPath) {
      return await push(defaultPath);
    }

    return null;
  };

  if (
    resultCode === 'Pending' &&
    paymentType === PAYMENT_TYPES.PAYPAL &&
    handleAction &&
    transactionData?.action
  ) {
    markPaymentRedirectExpected();
    return handleAction(transactionData.action);
  }

  switch (resultCode) {
    case 'Authorised':
      onAuthorize?.(transactionData);
      return await redirect('Authorised');

    case 'ChallengeShopper':
    case 'IdentifyShopper':
    case 'RedirectShopper': {
      const actionData: ExtendedPaymentAction | undefined =
        transactionData?.action;

      if (!actionData) {
        capturePaymentAnomaly({
          failureReason: 'missing_additional_action_data',
          resultCode,
          source: 'payment.handle_transaction_response',
        });
        throw new Error(
          'Missing `action` data in transaction. Cannot handle additional action.',
        );
      }

      markPaymentRedirectExpected();

      if (handleAction) {
        return handleAction(actionData);
      }

      if (actionData.method === 'GET') {
        return handleGetAction(actionData);
      } else if (actionData.method?.toUpperCase() === 'POST') {
        return handlePostAction(actionData, errorHandler);
      }

      capturePaymentAnomaly({
        failureReason: 'unsupported_additional_action_method',
        resultCode,
        source: 'payment.handle_transaction_response',
      });
      throw new Error(`Unhandled '${resultCode}' action.`);
    }
    case 'Refused':
      setIsTransactionSuccessfully?.(false);
      handleRefusedMessage(
        errorHandler,
        paymentType,
        transactionData?.refusalReasonCode,
      );
      return false;

    default:
      capturePaymentAnomaly({
        failureReason: 'unexpected_transaction_result',
        refusalReasonCode: transactionData?.refusalReasonCode,
        resultCode,
        source: 'payment.handle_transaction_response',
      });
      errorHandler([{ code: 'Error' }], { errorType: 'adyen' });
      return await redirect('default');
  }
};

export const handleRefusedMessage = (
  errorHandler: GenericErrorHandler,
  paymentType?: PaymentType,
  refusalReasonCode?: string,
) => {
  if (
    paymentType === 'cembra_invoice' ||
    paymentType === 'cembra_installment'
  ) {
    errorHandler(
      [
        {
          code: 'Refused',
        },
      ],
      { errorType: 'cembra' },
    );
  } else {
    // Store the refusal code in the message field.
    // It will be translated into the expected user-facing message later.
    errorHandler([{ code: 'Refused', message: refusalReasonCode }], {
      errorType: 'adyen',
    });
  }
};

const handleGetAction = (actionData: AdyenPaymentAction) => {
  if (actionData.type === 'redirect') {
    window.location.assign(actionData.url!);
  }
};

const handlePostAction = async (
  actionData: ExtendedPaymentAction,
  errorHandler: GenericErrorHandler,
) => {
  if (!actionData.data || !actionData.url) {
    capturePaymentAnomaly({
      failureReason: 'invalid_post_action_data',
      source: 'payment.handle_transaction_response',
    });
    throw new Error(`Unhandled post action.`);
  } else if (actionData?.paymentMethodType === PAYMENT_TYPES.SMBC) {
    return await handleSmbcPostAction(
      actionData.url,
      actionData.data,
      errorHandler,
    );
  } else if (actionData?.paymentMethodType === PAYMENT_TYPES.ORICO_FINANCING) {
    return await handleOricoPostAction(actionData.data, errorHandler);
  } else {
    capturePaymentAnomaly({
      failureReason: 'unsupported_post_action_payment_method',
      source: 'payment.handle_transaction_response',
    });
    throw new Error(`Unhandled post action.`);
  }
};

const postFormForPostAction = async (
  url: string,
  actionData: Record<string, string>,
) => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = url;

  // Add all form fields
  for (const [key, value] of Object.entries(actionData)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = value;
    form.appendChild(input);
  }

  // Add form to body and submit
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

const handleSmbcPostAction = async (
  url: string,
  actionData: Record<string, string>,
  errorHandler: GenericErrorHandler,
) => {
  try {
    return await postFormForPostAction(url, actionData);
  } catch (error) {
    errorHandler([{ code: 'Error' }], { errorType: 'adyen' });
  }
};

const handleOricoPostAction = async (
  actionData: Record<string, string>,
  errorHandler: GenericErrorHandler,
) => {
  try {
    await fetchAndSetCookies();
    return postFormForPostAction(ORICO_PAYMENT_URL, actionData);
  } catch (error) {
    errorHandler([{ code: 'Error' }], { errorType: 'adyen' });
  }
};

export const isAuthorisedRedirect = (query: ParsedUrlQuery) => {
  const resultCode = query?.[
    PAYMENT_QUERY_PARAMS.additionalAction
  ] as ResultCode;
  return resultCode === 'Authorised';
};

/**
 * This should check the last transaction when the backend will be fixed and will
 * properly create the last event which should be success. Currently it
 * sometimes returns events like:
 * "AUTHORIZATION_FAILURE"
 * "AUTHORIZATION_SUCCESS"
 * "AUTHORIZATION_REQUEST"
 */
export const isAuthorizedTransactions = (
  transaction: Maybe<TransactionItemFragment>,
) => {
  const lastTransaction = transaction?.events ?? [];
  return lastTransaction.some(
    (event) => event?.type === 'AUTHORIZATION_SUCCESS',
  );
};

export const isSuccessfulTransaction = (
  transaction: Maybe<TransactionItemFragment>,
) => {
  const lastTransaction = transaction?.events ?? [];
  return lastTransaction.some((event) => event?.type?.endsWith('SUCCESS'));
};

export const isFailedTransaction = (
  transaction: Maybe<TransactionItemFragment>,
) => {
  const lastTransaction = transaction?.events?.[0];
  return lastTransaction?.type?.endsWith('FAILURE');
};

export const isTransactionWithAdditionalAction = (
  transaction: Maybe<
    | TransactionInitializeMutation['transactionInitialize']
    | TransactionProcessMutation['transactionProcess']
  >,
) => !!transaction?.data?.action;

export const calculateDepositAmount = (
  total: number,
  deposit_percentage: number,
) => Number(((total * deposit_percentage) / 100).toFixed(2));

export const extractMulticardNextPayment = (
  query: ParsedUrlQuery,
): MulticardConfig | undefined => {
  const resultCode = query?.[
    PAYMENT_QUERY_PARAMS.additionalAction
  ] as ResultCode;
  if (resultCode == 'Authorised') {
    return {
      maxPayments: Number(
        query[PAYMENT_QUERY_PARAMS.successMulticardMaxPayments] as string,
      ),
      maxAmount: Number(
        query[PAYMENT_QUERY_PARAMS.successMulticardMaxAmount] as string,
      ),
      minAmount: Number(
        query[PAYMENT_QUERY_PARAMS.successMulticardMinAmount] as string,
      ),
    } as MulticardConfig;
  } else if (resultCode == 'Error' || resultCode == 'Refused') {
    return {
      maxPayments: Number(
        query[PAYMENT_QUERY_PARAMS.failureMulticardMaxPayments] as string,
      ),
      maxAmount: Number(
        query[PAYMENT_QUERY_PARAMS.failureMulticardMaxAmount] as string,
      ),
      minAmount: Number(
        query[PAYMENT_QUERY_PARAMS.failureMulticardMinAmount] as string,
      ),
    } as MulticardConfig;
  }
};

export const getMulticardHasNextPayment = (query: ParsedUrlQuery) => {
  const multicardNextPayment = extractMulticardNextPayment(query);
  return !!(
    multicardNextPayment?.maxPayments && multicardNextPayment?.maxPayments > 0
  );
};

export const getNextMulticardPaymentToken = <
  T extends Maybe<Record<string, any>>,
>(
  transactionData: T,
) => {
  const maxPayments = Number(transactionData?.breitling?.multicardMaxPayments);
  if (maxPayments > 0) {
    return transactionData?.breitling?.payPerLinkToken;
  }
};

export const checkShouldRedirectToDelivery = (
  shippingAddress: CheckoutFragment['shippingAddress'],
  billingAddress: CheckoutFragment['billingAddress'],
  deliveryMode?: string,
  boutiqueExtRef?: string,
  isShippingRequired?: boolean,
): boolean => {
  return (
    (!shippingAddress && isShippingRequired) ||
    !billingAddress ||
    (!deliveryMode && isShippingRequired) ||
    (deliveryMode === 'BOUTIQUE_PICKUP' && !boutiqueExtRef)
  );
};

export const getInstallmentCount = (type: Maybe<PaymentType>) => {
  const match = type?.match(oneyPaymentTypePattern);
  if (match) {
    return Number(match[1]);
  }
  return 1;
};

export const getInstallmentAmount = (total: number, type: Maybe<PaymentType>) =>
  new Decimal(total).div(getInstallmentCount(type)).toNumber();

export const isNonAdyenPaymentMethod = (type: PaymentType | undefined | null) =>
  type && NON_ADYEN_PAYMENT_METHODS?.includes(type);

interface NonAdyenGatewayCheckoutOpts {
  paymentType: PaymentType;
}
export interface INonAdyenGatewayCheckout {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getState: () => any;
  isValid: () => boolean;
  paymentType: PaymentType;
  setIsValid: (b: boolean) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setState: (state: any) => void;
  setValidateFunction: (fun: () => boolean) => void;
  unmount: () => void;
}

export const NonAdyenGatewayCheckout = ({
  paymentType,
}: NonAdyenGatewayCheckoutOpts): INonAdyenGatewayCheckout => {
  let state: object = {};
  let validated = false;
  let validateFun: (() => boolean) | null = null;
  const setState = (newState: object) => {
    state = { ...state, ...newState };
  };
  const getState = () => {
    return state;
  };
  const setValidateFunction = (fun: () => boolean) => {
    validateFun = fun;
  };
  const setIsValid = (b: boolean) => {
    validated = b;
  };
  const isValid = () => {
    if (validateFun) {
      return validateFun();
    }
    return validated;
  };

  return {
    paymentType,
    getState,
    setState,
    setIsValid,
    isValid,
    setValidateFunction,
    unmount: () => {
      //do nothing, compatible with old code
    },
  };
};
